import { useEffect, useMemo, useRef, useState } from 'react'

import { useQuery } from '@api/hooks'
import TableOverlays from '@components/TableOverlays/TableOverlays'
import { MarketBasketExclusion } from '@domain'
import usePageParams from '@hooks/usePageParams'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { DataGridPro, GridColDef, GridSortDirection } from '@mui/x-data-grid-pro'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import SalesforceExclusionFilters from './SalesforceExclusionFilters'
import { getColumns } from './SalesforceExclusionsTable.utils'

type Props = {
	updatedSalesforceExclusions: MarketBasketExclusion[]
	setUpdatedSalesforceExclusions: React.Dispatch<React.SetStateAction<MarketBasketExclusion[]>>
}

export function SalesforceExclusionTable({
	updatedSalesforceExclusions,
	setUpdatedSalesforceExclusions,
}: Props) {
	const [customer] = useQueryParam('exclusionCustomer', StringParam)
	const [manufacturer] = useQueryParam('exclusionManufacturer', StringParam)
	const [enabled] = useQueryParam('exclusionEnabled', StringParam)
	const [program] = useQueryParam('exclusionProgram', StringParam)
	const [source] = useQueryParam('exclusionSource', StringParam)

	const [page, pageSize, setPageParams] = usePageParams()

	const [sortBy, setSortBy] = useQueryParam('exclusionSortBy', withDefault(StringParam, 'customer'))
	const [sortDirection, setSortDirection] = useQueryParam(
		'exclusionSortDirection',
		withDefault(StringParam, 'ASC')
	)

	// Integrates updated exclusions into data fetched from api to show updated enabled values in table
	const [mergedExclusions, setMergedExclusions] = useState([])

	const { marketBasketId } = useParams()

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketsKeys.salesforceExclusions(marketBasketId, {
			page,
			pageSize,
			sortBy,
			sortDirection: sortDirection as 'ASC' | 'DESC',
			customer,
			manufacturer,
			enabled,
			program,
			source,
		}),
		queryFn: () =>
			MarketBasketsApi.salesforceExclusions(marketBasketId, {
				page,
				pageSize,
				sortBy,
				sortDirection: sortDirection as 'ASC' | 'DESC',
				customer,
				manufacturer,
				enabled,
				program,
				source,
			}),
		enabled: !!marketBasketId,
	})

	const exclusions = data?.data
	const pagination = data?.pagination

	const rowCountRef = useRef(pagination?.total || 0)

	const rowCount = useMemo(() => {
		if (pagination?.total !== undefined) {
			rowCountRef.current = pagination?.total
		}
		return rowCountRef.current
	}, [pagination?.total])

	useEffect(() => {
		const newMergedExclusions = exclusions?.map((exclusion) => {
			if (!updatedSalesforceExclusions.some((item) => item.id === exclusion.id)) {
				return exclusion
			}
			return {
				...exclusion,
				...updatedSalesforceExclusions.find((item) => item.id === exclusion.id),
			}
		})
		setMergedExclusions(newMergedExclusions)
	}, [updatedSalesforceExclusions, exclusions])

	const columns = useMemo(
		() => getColumns(setUpdatedSalesforceExclusions),
		[setUpdatedSalesforceExclusions]
	)

	return (
		<Stack spacing={2}>
			<Typography variant="h6">Salesforce Exclusions</Typography>
			<Divider />
			<SalesforceExclusionFilters />
			<Box sx={{ maxHeight: '40rem' }}>
				<DataGridPro
					density="compact"
					loading={isLoading}
					rows={mergedExclusions || []}
					columns={columns as GridColDef<MarketBasketExclusion>[]}
					// Pagination
					pageSizeOptions={[100]}
					rowCount={rowCount}
					pagination
					paginationMode="server"
					// DataGrid pages start at 0, but our API starts at 1
					paginationModel={{ page: page - 1, pageSize }}
					onPaginationModelChange={(model) => {
						setPageParams({ page: model.page + 1 })
					}}
					// Sorting
					sortingMode="server"
					onSortModelChange={(model) => {
						if (isLoading) return
						setSortBy(model[0]?.field)
						setSortDirection(model[0]?.sort.toUpperCase() as 'ASC' | 'DESC')
					}}
					sortModel={[{ field: sortBy, sort: sortDirection?.toLowerCase() as GridSortDirection }]}
					slots={{
						noRowsOverlay: TableOverlays.NoRowsOverlay,
						noResultsOverlay: TableOverlays.NoRowsOverlay,
					}}
					slotProps={{
						noRowsOverlay: { message: 'No salesforce exclusions found' },
						noResultsOverlay: { message: 'No salesforce exclusions found' },
					}}
					sx={{
						'& .MuiTablePagination-root': {
							overflowY: 'hidden',
						},
					}}
					disableColumnMenu
					disableColumnReorder
					disableColumnResize
					disableRowSelectionOnClick
				/>
			</Box>
		</Stack>
	)
}
